import produce from 'immer';
import { useCallback, useEffect, useReducer, useRef } from 'react';
import { isObject, timestampByUTC } from '../helpers/common';
import { LocalStorage } from '../helpers/localStorage';
import type { RequestConfig, RequestFn } from '../types/api';
import { useMount, useMutable } from './common';

interface ApiState<P> {
  pending: boolean;
  data: P | undefined;
  error: any | undefined;
  requestCount: number;
}

type ApiAction<P> =
  | {
      type: 'PENDING';
      payload: boolean;
    }
  | {
      type: 'LOADED';
      payload: P;
    }
  | {
      type: 'ERROR';
      payload: any | undefined;
    }
  | {
      type: 'RESET';
      payload: ApiState<P>;
    }
  | { type: 'RESET_REQUEST_COUNT' };

export enum ApiError {
  UNMOUNTED = 'unmounted',
  CANCELED = 'canceled',
}

function mergeData(prev: any, next: any) {
  if (isObject(prev) && isObject(next)) {
    return { ...prev, ...next };
  }

  if (Array.isArray(prev) && Array.isArray(next)) {
    return [...prev, ...next];
  }

  return next;
}

type Logger = (values: any) => void;

type SaveErrorsCountToStorage = {
  storageName: string;
  timeLeftToClear: number;
};

type ErrorCountStorageType = { createdAt: number; requestCount: number };

export interface UseApiConfig {
  cleanUp?: boolean;
  mergeData?: boolean;
  countErrors?: boolean;
  cancelPrevious?: boolean;
  pendingOnSuccess?: boolean;
  clearDataOnError?: boolean;
  dispatchOnCancel?: boolean;
  logger?: Logger[] | Logger;
  requestConfig?: RequestConfig;
  saveErrorsCountToStorage?: SaveErrorsCountToStorage;
}

export function useApi<Args extends any[], Data>(
  fn: RequestFn<Args, Data>,
  initialState?: Partial<ApiState<Data>>,
  hookConfig?: UseApiConfig
) {
  const hookConfigRef = useMutable<UseApiConfig>({
    cleanUp: true,
    mergeData: false,
    countErrors: true,
    cancelPrevious: true,
    pendingOnSuccess: false,
    clearDataOnError: false,
    dispatchOnCancel: true,
    logger: undefined,
    ...hookConfig,
  });

  const _initialState = {
    pending: false,
    data: undefined,
    error: undefined,
    requestCount: (() => {
      if (hookConfigRef.current.saveErrorsCountToStorage) {
        const errorsCountLocalStorage = new LocalStorage<
          ErrorCountStorageType,
          null
        >(
          `errors-count-new-${hookConfigRef.current.saveErrorsCountToStorage.storageName}`,
          null
        );

        const values = errorsCountLocalStorage.get();

        if (
          values != null &&
          timestampByUTC() - values.createdAt <=
            hookConfigRef.current.saveErrorsCountToStorage.timeLeftToClear
        ) {
          return values.requestCount;
        }

        errorsCountLocalStorage.remove();
      }

      return 0;
    })(),
    ...initialState,
  };

  function apiReducer(
    state: ApiState<Data>,
    action: ApiAction<Data>
  ): ApiState<Data> {
    return produce(state, (draft) => {
      switch (action.type) {
        case 'PENDING': {
          draft.pending = action.payload;
          break;
        }
        case 'LOADED': {
          if (!hookConfigRef.current.pendingOnSuccess) {
            draft.pending = false;
          }
          draft.data = hookConfigRef.current.mergeData
            ? mergeData(draft.data, action.payload)
            : action.payload;
          draft.error = undefined;
          draft.requestCount += 1;
          break;
        }
        case 'ERROR': {
          draft.pending = false;
          draft.error = action.payload;
          if (hookConfigRef.current.countErrors) {
            draft.requestCount += 1;
          }
          if (hookConfigRef.current.clearDataOnError) {
            draft.data = undefined;
          }
          if (hookConfigRef.current.logger != null) {
            if (Array.isArray(hookConfigRef.current.logger)) {
              hookConfigRef.current.logger.forEach((logger) => {
                logger(action.payload);
              });
            } else {
              hookConfigRef.current.logger(action.payload);
            }
          }

          if (hookConfigRef.current.saveErrorsCountToStorage) {
            const { storageName } =
              hookConfigRef.current.saveErrorsCountToStorage;

            const errorsCountLocalStorage = new LocalStorage<
              ErrorCountStorageType,
              null
            >(`errors-count-new-${storageName}`, null);

            const valueErrorsCountLocalStorage = errorsCountLocalStorage.get();

            const timestamp = timestampByUTC();

            if (valueErrorsCountLocalStorage == null) {
              errorsCountLocalStorage.replace({
                createdAt: timestamp,
                requestCount: draft.requestCount,
              });
              return;
            }

            errorsCountLocalStorage.replace({
              createdAt: valueErrorsCountLocalStorage.createdAt,
              requestCount: draft.requestCount,
            });
          }

          break;
        }
        case 'RESET_REQUEST_COUNT': {
          if (hookConfigRef.current.saveErrorsCountToStorage) {
            const { storageName } =
              hookConfigRef.current.saveErrorsCountToStorage;

            const errorsCountLocalStorage = new LocalStorage<
              ErrorCountStorageType,
              null
            >(`errors-count-new-${storageName}`, null);

            errorsCountLocalStorage.remove();
          }
          draft.requestCount = 0;
          break;
        }
        case 'RESET': {
          return action.payload;
        }
      }
    });
  }

  const [state, dispatch] = useReducer(apiReducer, _initialState);

  const cancelRequest = useRef<() => void>();

  function tryCancelRequest() {
    if (cancelRequest && cancelRequest.current) {
      cancelRequest.current();
    }
  }

  const isMountedRef = useMount();

  const callApi = useCallback(
    (...args: Args) => {
      if (hookConfigRef.current.cancelPrevious) {
        tryCancelRequest();
      }

      const abortController = new AbortController();

      const request = (async () => {
        dispatch({ type: 'PENDING', payload: true });

        try {
          const response = await fn.apply(
            {
              ...hookConfigRef.current.requestConfig,
              signal: abortController.signal,
            },
            args
          );

          cancelRequest.current = undefined;

          if (isMountedRef.current) {
            dispatch({
              type: 'LOADED',
              payload: response.data,
            });
          }

          return { response, error: undefined };
        } catch (e) {
          cancelRequest.current = undefined;

          if (!isMountedRef.current) {
            return {
              response: undefined,
              error: new Error(ApiError.UNMOUNTED),
            };
          }

          if (e instanceof Error && e.name === 'AbortError') {
            if (hookConfigRef.current.dispatchOnCancel) {
              dispatch({
                type: 'ERROR',
                payload: {
                  message: 'canceled',
                  data: undefined,
                },
              });
            }

            return { response: undefined, error: new Error(ApiError.CANCELED) };
          }

          dispatch({
            type: 'ERROR',
            payload: (e as any).data,
          });

          return { response: undefined, error: e };
        }
      })();

      cancelRequest.current = () => abortController.abort();

      return {
        request,
        cancel: tryCancelRequest,
      };
    },
    [fn, hookConfigRef, isMountedRef]
  );

  useEffect(
    () => () => {
      if (hookConfigRef.current.cleanUp) tryCancelRequest();
    },
    [hookConfigRef]
  );

  const initialStateRef = useMutable(_initialState);

  const reset = useCallback(
    (state: ApiState<Data> = initialStateRef.current) => {
      tryCancelRequest();
      dispatch({ type: 'RESET', payload: state });
    },
    [initialStateRef]
  );

  const resetRequestCount = useCallback(() => {
    tryCancelRequest();
    dispatch({ type: 'RESET_REQUEST_COUNT' });
  }, []);

  return [
    callApi,
    state,
    {
      reset,
      resetRequestCount,
      cancel: useCallback(tryCancelRequest, []),
      dispatch,
    },
  ] as const;
}
